<script setup lang="ts">
import type { ForecastTargetEvaluation } from '../internal/types.ts'
import { formatDate } from '@taler/domain'
import { Select, StatusTag } from '@taler/ui'
import { computed } from 'vue'
import { useI18n } from 'vue-i18n'
import type { Target } from '@/features/targets'

const props = defineProps<{
  targets: readonly Target[]
  modelValue: string | undefined
  evaluation: ForecastTargetEvaluation | undefined
}>()
const emit = defineEmits<{ 'update:modelValue': [value: string | undefined] }>()

const { t, locale } = useI18n()
const localeCode = computed(() => locale.value as 'en' | 'hu')

// Only NET_WORTH targets are projectable - see AGENTS.md: the forecast has no
// per-account or per-category attribution the other four metrics would need.
const netWorthTargets = computed(() => props.targets.filter((target) => target.metric === 'NET_WORTH'))
const options = computed(() =>
  netWorthTargets.value.map((target) => ({ value: target.id, label: target.name })),
)

const statusTone = computed(() =>
  props.evaluation === undefined ? 'neutral' : props.evaluation.status === 'MET' ? 'ok' : 'danger',
)
</script>

<template>
  <div v-if="netWorthTargets.length > 0" class="flex flex-col gap-[var(--space-inline)]">
    <label class="flex items-center gap-2 text-[length:var(--text-step-2)] text-[color:var(--color-text)]">
      <span class="text-[color:var(--color-text-muted)]">{{ t('forecast.checkAgainstTarget') }}</span>
      <Select
        :model-value="modelValue"
        :options="options"
        :placeholder="t('forecast.chooseTarget')"
        @update:model-value="emit('update:modelValue', $event)"
      />
    </label>

    <StatusTag v-if="evaluation" :tone="statusTone">
      {{
        evaluation.status === 'MET'
          ? t('forecast.targetReachedBy', { date: formatDate(evaluation.asOf, localeCode) })
          : t('forecast.targetNotReached')
      }}
    </StatusTag>
  </div>
</template>
